import React, { useState } from 'react';

import { Alert, Button, Container, Input } from '@meu-mapa-nev/design-system';

export type EtapaLogin = 'senha' | 'segundo-passo';

export interface TelaDeLoginAdminProps {
  readonly etapa: EtapaLogin;
  readonly onSubmitPassword: (email: string, password: string) => void;
  readonly onSubmitCode: (code: string) => void;
  readonly submitting?: boolean;
  readonly erro?: string | undefined;
  /** Espaço onde o desafio antibot é desenhado, na etapa da senha. */
  readonly desafioAntibot?: React.ReactNode;
}

/**
 * Login da administração: senha e depois segundo passo por código.
 *
 * O segundo passo NÃO é opcional. É ele que garante que uma senha vazada,
 * isoladamente, não abra o painel com dados de todas as participantes.
 *
 * Desktop-first, mas em coluna única para que a mesma tela funcione no celular
 * em sequência linear, como as fontes exigem para as ações essenciais.
 */
export const TelaDeLoginAdmin: React.FC<TelaDeLoginAdminProps> = ({
  etapa,
  onSubmitPassword,
  onSubmitCode,
  submitting = false,
  erro,
  desafioAntibot,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  return (
    <Container size="reading">
      <h1
        style={{
          fontFamily: 'var(--nev-font-heading)',
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'var(--nev-text-primary)',
          marginBottom: 'var(--nev-space-4)',
        }}
      >
        {etapa === 'senha' ? 'Administração — entrar' : 'Confirmação em duas etapas'}
      </h1>

      {erro !== undefined && <Alert type="critical">{erro}</Alert>}

      {etapa === 'senha' ? (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitPassword(email, password);
          }}
        >
          <Input
            label="E-mail"
            type="email"
            autoComplete="username"
            value={email}
            required
            disabled={submitting}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />

          <Input
            label="Senha"
            type="password"
            autoComplete="current-password"
            value={password}
            required
            disabled={submitting}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />

          {desafioAntibot !== undefined && (
            <div style={{ marginBottom: 'var(--nev-space-4)' }}>{desafioAntibot}</div>
          )}

          <Button type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Verificando…' : 'Continuar'}
          </Button>
        </form>
      ) : (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitCode(code);
          }}
        >
          <Alert type="info">
            Enviamos um código para o seu e-mail. A senha sozinha não conclui a entrada.
          </Alert>

          <Input
            label="Código de 6 dígitos"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            required
            disabled={submitting}
            helperText="O código vale por 10 minutos e pode ser colado."
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
            }}
          />

          <Button type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Confirmando…' : 'Entrar'}
          </Button>
        </form>
      )}
    </Container>
  );
};
